import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import {
  resolveIncludedExcludedModules,
  type ActivePermission,
} from 'src/pages/subscriptions/productPermissionModules';
import type { CatalogueItem, PackageIncludedProduct } from './types';

const FeatureList = ({
  items,
  tone,
}: {
  items: string[];
  tone: 'included' | 'excluded';
}) => {
  const isIncluded = tone === 'included';
  return (
    <Box
      style={{
        flex: 1,
        backgroundColor: isIncluded ? '#ECFDF5' : '#FEF2F2',
        borderRadius: 8,
        padding: 16,
        minHeight: 120,
      }}>
      {items.length === 0 ? (
        <Typography style={{ fontSize: 13, color: '#9CA3AF' }}>
          {isIncluded ? 'No modules included' : 'No modules excluded'}
        </Typography>
      ) : (
        items.map((item) => (
          <Box
            key={`${tone}-${item}`}
            display="flex"
            alignItems="center"
            style={{ gap: 8, marginBottom: 8 }}>
            {isIncluded ? (
              <CheckCircleOutlineIcon style={{ fontSize: 18, color: '#059669' }} />
            ) : (
              <HighlightOffIcon style={{ fontSize: 18, color: '#DC2626' }} />
            )}
            <Typography style={{ fontSize: 13, color: '#374151' }}>{item}</Typography>
          </Box>
        ))
      )}
    </Box>
  );
};

type PackageProductBreakdown = PackageIncludedProduct & {
  included: string[];
  excluded: string[];
};

const PackageIncludedProductsPanel = ({
  packageName,
  products,
}: {
  packageName: string;
  products: PackageProductBreakdown[];
}) => (
  <Box
    style={{
      backgroundColor: '#F8FAFC',
      borderRadius: 8,
      padding: 16,
      border: '1px solid #E5E7EB',
    }}>
    <Typography
      style={{
        fontSize: 14,
        fontWeight: 700,
        marginBottom: 12,
        color: '#111827',
      }}>
      Products included in {packageName}:
    </Typography>
    {products.length === 0 ? (
      <Typography style={{ fontSize: 13, color: '#9CA3AF' }}>
        No nested products returned for this package
      </Typography>
    ) : (
      products.map((product, index) => {
        const hasModuleBreakdown =
          product.included.length > 0 || product.excluded.length > 0;
        return (
          <Box
            key={product.id}
            style={{
              marginBottom: index === products.length - 1 ? 0 : 16,
              paddingBottom: index === products.length - 1 ? 0 : 16,
              borderBottom:
                index === products.length - 1 ? 'none' : '1px solid #E5E7EB',
            }}>
            <Typography style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>
              {index + 1}. {product.name}
            </Typography>
            {hasModuleBreakdown ? (
              <Box
                display="grid"
                gridTemplateColumns="1fr 1fr"
                style={{ gap: 12, marginTop: 10 }}>
                <FeatureList items={product.included} tone="included" />
                <FeatureList items={product.excluded} tone="excluded" />
              </Box>
            ) : null}
          </Box>
        );
      })
    )}
  </Box>
);

interface ItemModuleDetailsProps {
  item: CatalogueItem;
  activePermissions: ActivePermission[];
}

export const ItemModuleDetails = ({ item, activePermissions }: ItemModuleDetailsProps) => {
  if (item.entityType === 'PACKAGE') {
    const products = (item.includedProducts || []).map((nested) => {
      const { included, excluded } = resolveIncludedExcludedModules(
        activePermissions,
        nested.solution,
        nested.permissionIds,
      );
      return { ...nested, included, excluded };
    });
    return (
      <Box style={{ marginTop: 12 }}>
        <PackageIncludedProductsPanel packageName={item.label} products={products} />
      </Box>
    );
  }

  const { included, excluded } = resolveIncludedExcludedModules(
    activePermissions,
    item.solution,
    item.permissionIds,
  );

  return (
    <Box display="grid" gridTemplateColumns="1fr 1fr" style={{ gap: 12, marginTop: 12 }}>
      <FeatureList items={included} tone="included" />
      <FeatureList items={excluded} tone="excluded" />
    </Box>
  );
};

interface ModuleBreakdownProps {
  selectedItems: CatalogueItem[];
  activePermissions: ActivePermission[];
}

export const ModuleBreakdown = ({
  selectedItems,
  activePermissions,
}: ModuleBreakdownProps) => {
  const productCards: Array<CatalogueItem & { included: string[]; excluded: string[] }> =
    [];
  const packageCards: Array<{
    id: string;
    label: string;
    products: PackageProductBreakdown[];
  }> = [];
  const seen = new Set<string>();

  selectedItems.forEach((selected) => {
    if (seen.has(selected.value)) return;
    seen.add(selected.value);

    if (selected.entityType === 'PACKAGE') {
      const products = (selected.includedProducts || []).map((nested) => {
        const { included, excluded } = resolveIncludedExcludedModules(
          activePermissions,
          nested.solution,
          nested.permissionIds,
        );
        return { ...nested, included, excluded };
      });
      packageCards.push({
        id: selected.value,
        label: selected.label,
        products,
      });
      return;
    }

    const { included, excluded } = resolveIncludedExcludedModules(
      activePermissions,
      selected.solution,
      selected.permissionIds,
    );
    if (included.length === 0 && excluded.length === 0) return;
    productCards.push({ ...selected, included, excluded });
  });

  if (productCards.length === 0 && packageCards.length === 0) return null;

  return (
    <Box display="flex" flexDirection="column" style={{ gap: 24, marginTop: 8 }}>
      {productCards.map((product) => (
        <Box key={product.id}>
          <Typography
            style={{
              fontSize: 15,
              fontWeight: 700,
              marginBottom: 12,
              color: '#111827',
            }}>
            {product.label}
          </Typography>
          <Box display="grid" gridTemplateColumns="1fr 1fr" style={{ gap: 16 }}>
            <FeatureList items={product.included} tone="included" />
            <FeatureList items={product.excluded} tone="excluded" />
          </Box>
        </Box>
      ))}
      {packageCards.map((pkg) => (
        <Box key={pkg.id}>
          <Typography
            style={{
              fontSize: 15,
              fontWeight: 700,
              marginBottom: 12,
              color: '#111827',
            }}>
            {pkg.label}
          </Typography>
          <PackageIncludedProductsPanel packageName={pkg.label} products={pkg.products} />
        </Box>
      ))}
    </Box>
  );
};
